import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import {
  CreditCard, Calculator, Chrome, MapPin, LineChart, Search,
  Facebook, MessageCircle, MessagesSquare, Sparkles, Cloud, Rocket, Github, Plug
} from "lucide-react";

const ICONS = {
  stripe: CreditCard, quickbooks: Calculator, google_workspace: Chrome,
  google_business: MapPin, google_analytics: LineChart, google_search_console: Search,
  meta: Facebook, whatsapp: MessageCircle, twilio: MessagesSquare,
  openai: Sparkles, cloudflare: Cloud, vercel: Rocket, github: Github,
};

export default function Integrations() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => { const { data } = await api.get("/integrations"); setItems(data); };
  useEffect(() => { load(); }, []);

  const toggle = async (it) => {
    if (it.status === "coming_soon") { toast.info(`${it.name} — ${t("integrations.coming_soon")}`); return; }
    setBusy(it.key);
    try {
      if (it.connected) { await api.post(`/integrations/${it.key}/disconnect`); toast.success(`${it.name} disconnected`); }
      else { await api.post(`/integrations/${it.key}/connect`); toast.success(`${it.name} connected`); }
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(null); }
  };

  return (
    <DashboardLayout title={t("integrations.title")} subtitle={t("integrations.subtitle")}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="integrations-grid">
        {items.map((it) => {
          const Icon = ICONS[it.key] || Plug;
          const isSoon = it.status === "coming_soon";
          return (
            <div key={it.key} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 flex flex-col" data-testid={`integration-card-${it.key}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-zinc-800 grid place-items-center"><Icon className="w-5 h-5 text-zinc-100" /></div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest ${it.connected ? "text-emerald-400" : isSoon ? "text-zinc-500" : "text-zinc-400"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${it.connected ? "bg-emerald-500 glow-green" : isSoon ? "bg-zinc-600" : "bg-zinc-400"}`} />
                  {it.connected ? t("integrations.connected") : isSoon ? t("integrations.coming_soon") : t("integrations.available")}
                </span>
              </div>
              <div className="mt-4">
                <div className="font-display text-sm font-semibold text-zinc-100">{it.name}</div>
                <div className="text-xs text-zinc-500 mt-1">{it.description}</div>
              </div>
              <div className="mt-5">
                <button
                  data-testid={`integration-toggle-${it.key}`}
                  disabled={busy === it.key || isSoon}
                  onClick={() => toggle(it)}
                  className={`btn-scale w-full h-9 rounded-md text-xs font-medium disabled:opacity-50 ${it.connected ? "border border-zinc-700 text-zinc-100 hover:bg-zinc-900" : "bg-white text-zinc-950 hover:bg-zinc-100"}`}
                >
                  {isSoon ? t("integrations.coming_soon") : it.connected ? t("integrations.disconnect") : t("integrations.connect")}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
